import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api, { TenantDetail, PlanEntitlement, getPlans, extendTrial, convertTrial, getInitialAdminCredentials, resetInitialAdminCredentials, attachCustomDomain, getTenantDnsRecords, InitialAdminCredentials, TenantDnsRecords } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useBrand } from '../contexts/BrandContext';
import { BrandingSection } from '../components/BrandingSection';
import { AdminEmailInline } from '../components/AdminEmailInline';
import { ContractSection } from '../components/ContractSection';
import { LicenseSection } from '../components/LicenseSection';
import { AiSettingsSection } from '../components/AiSettingsSection';
import { TenantAssignmentSection } from '../components/TenantAssignmentSection';
import { ServicePackagePanel } from '../components/ServicePackagePanel';
import { VdiSection } from '../components/VdiSection';
import { WebhostingSection } from '../components/WebhostingSection';
import { PlanChangeCostModal } from '../components/PlanChangeCostModal';
import { BillingSection } from '../components/BillingSection';

export function TenantDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { operator } = useAuth();
  const { domain } = useBrand();
  const [tenant, setTenant] = useState<TenantDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Plan catalog (for the cost-disclosure modal) + the plan the operator
  // picked but has NOT yet confirmed — the dropdown no longer mutates
  // directly; it opens PlanChangeCostModal first.
  const [plans, setPlans] = useState<PlanEntitlement[]>([]);
  const [pendingPlan, setPendingPlan] = useState<string | null>(null);
  // Initial tenant-admin credentials: revealed exactly once (the backend
  // destroys the source on read). `adminCreds` holds the one-time value;
  // `credsMsg` carries the "already retrieved / unavailable" notice.
  const [adminCreds, setAdminCreds] = useState<InitialAdminCredentials | null>(null);
  const [credsMsg, setCredsMsg] = useState<string | null>(null);
  // DNS records the operator must enter manually in Core-Networks (until DNS
  // automation is enabled). Loaded read-only on mount.
  const [dns, setDns] = useState<TenantDnsRecords | null>(null);
  useEffect(() => { getTenantDnsRecords(slug as string).then(setDns).catch(() => setDns(null)); }, [slug]);

  const revealAdminCredentials = async () => {
    setBusy(true); setCredsMsg(null); setErr(null);
    try {
      setAdminCreds(await getInitialAdminCredentials(slug as string));
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } }).response?.status;
      setCredsMsg(status === 410
        ? 'Bereits abgerufen (oder nicht verfügbar). Initial-Zugänge werden aus Sicherheitsgründen nur einmal angezeigt. Nutze „Zugang neu ausstellen", falls der Kunde sie verloren hat.'
        : (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Abruf fehlgeschlagen.');
    } finally { setBusy(false); }
  };

  const resetAdminCreds = async () => {
    setBusy(true); setCredsMsg(null); setErr(null); setAdminCreds(null);
    try {
      const { jobId } = await resetInitialAdminCredentials(slug as string);
      setCredsMsg(`Reset-Job ${jobId} gestartet. Nach Abschluss (~1 Min) erneut „Zugang anzeigen" klicken.`);
    } catch (e: unknown) {
      setCredsMsg((e as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Reset fehlgeschlagen.');
    } finally { setBusy(false); }
  };

  const [domainInput, setDomainInput] = useState('');
  const [domainMsg, setDomainMsg] = useState<string | null>(null);
  const attachDomain = async () => {
    const domain = domainInput.trim().toLowerCase();
    if (!domain) return;
    setBusy(true); setDomainMsg(null); setErr(null);
    try {
      const { jobId } = await attachCustomDomain(slug as string, domain);
      setDomainMsg(`Job ${jobId} gestartet. Voraussetzung: ${domain} zeigt per A/CNAME auf den Cluster-Ingress (sonst bleibt das Zertifikat pending).`);
      setDomainInput('');
    } catch (e: unknown) {
      setDomainMsg((e as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Anhängen fehlgeschlagen.');
    } finally { setBusy(false); }
  };

  const load = () => api.get<TenantDetail>(`/tenants/${slug}`).then((r) => setTenant(r.data));
  useEffect(() => { load(); }, [slug]);
  useEffect(() => { getPlans().then(setPlans).catch(() => setPlans([])); }, []);

  const callAction = async (path: string, body: unknown = {}) => {
    setBusy(true); setErr(null);
    try { await api.post(`/tenants/${slug}${path}`, body); await load(); }
    catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } }).response?.data?.error;
      setErr(msg ?? 'Aktion fehlgeschlagen');
    }
    finally { setBusy(false); }
  };

  const changePlan = async (toPlan: string, reason: string) => {
    setBusy(true); setErr(null);
    try { await api.put(`/tenants/${slug}/plan`, { plan: toPlan, reason }); setPendingPlan(null); await load(); }
    catch (e: unknown) {
      setErr((e as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Plan-Change fehlgeschlagen');
    }
    finally { setBusy(false); }
  };

  if (!tenant) return <p className="text-slate-500">Lade…</p>;

  const isReadOnly = !!operator?.readOnly;

  return (
    <div className="max-w-5xl">
      <h2 className="text-2xl font-semibold mb-1">{tenant.name}</h2>
      <p className="text-slate-500 font-mono mb-4">slug: {tenant.slug} · realm: {tenant.realmName}</p>

      {err && <div className="bg-red-100 text-red-700 p-3 rounded mb-3">{err}</div>}

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded shadow p-4">
          <h3 className="font-semibold mb-2">Status</h3>
          <p className="text-2xl font-mono">{tenant.status}</p>
          <p className="text-sm text-slate-500 mt-1">Plan: <strong>{tenant.plan}</strong></p>
          {tenant.trialEndsAt && (
            <div className="mt-2 rounded bg-sky-50 border border-sky-200 p-2 text-sm">
              <p className="text-sky-900">
                Testzugang bis <strong>{new Date(tenant.trialEndsAt).toLocaleDateString('de-DE')}</strong>
                {tenant.trialStage ? ` · Stufe: ${tenant.trialStage}` : ''}
              </p>
              <div className="flex gap-2 mt-2">
                <button
                  disabled={busy || isReadOnly}
                  className="btn-secondary"
                  onClick={async () => {
                    setBusy(true); setErr(null);
                    try { await extendTrial(tenant.slug, 30); await load(); }
                    catch (e: unknown) { setErr((e as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Verlängern fehlgeschlagen.'); }
                    finally { setBusy(false); }
                  }}
                >+30 Tage</button>
                <button
                  disabled={busy || isReadOnly}
                  className="btn-secondary"
                  title="Kein Ablauf mehr; Abrechnung unter Vertrag separat einschalten"
                  onClick={async () => {
                    if (!window.confirm('Testzugang in einen regulären Mandanten umwandeln? Ablauf und Erinnerungen entfallen.')) return;
                    setBusy(true); setErr(null);
                    try { await convertTrial(tenant.slug); await load(); }
                    catch (e: unknown) { setErr((e as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Umwandeln fehlgeschlagen.'); }
                    finally { setBusy(false); }
                  }}
                >In Vertrag umwandeln</button>
              </div>
            </div>
          )}
          <AdminEmailInline slug={tenant.slug} current={tenant.adminEmail} isReadOnly={isReadOnly} onChanged={load} />
          <p className="text-sm text-slate-500">Users (Keycloak): {tenant.userCount ?? '—'}</p>
          <p className="text-sm text-slate-500">
            Hosting: <strong>{tenant.hostingProvider}</strong>
            {tenant.hostingRegion ? ` · ${tenant.hostingRegion}` : ''}
          </p>
          {tenant.hostingProvider === 'custom' && tenant.hostingTarget && (
            <p className="text-xs text-slate-400 font-mono break-all">{tenant.hostingTarget}</p>
          )}
        </div>

        <div className="bg-white rounded shadow p-4">
          <h3 className="font-semibold mb-2">Aktionen</h3>
          <div className="flex flex-wrap gap-2">
            {tenant.status === 'active' && (
              <button onClick={() => callAction('/suspend')} disabled={busy || isReadOnly} className="btn-danger">Suspendieren</button>
            )}
            {tenant.status === 'suspended' && (
              <button onClick={() => callAction('/reactivate')} disabled={busy || isReadOnly} className="btn-primary">Reaktivieren</button>
            )}
            {/* Picking a plan does NOT mutate directly — it opens the
                cost-disclosure modal, which confirms + dispatches. */}
            <select
              disabled={busy || isReadOnly}
              value={tenant.plan}
              onChange={(e) => { if (e.target.value !== tenant.plan) setPendingPlan(e.target.value); }}
              className="px-2 py-1 rounded border"
            >
              <option value="basic">basic</option>
              <option value="business">business</option>
              <option value="enterprise">enterprise</option>
            </select>
          </div>
          {isReadOnly && <p className="text-xs text-slate-400 mt-2">Read-only-Operator; mutierende Aktionen deaktiviert.</p>}
        </div>
      </div>

      {/* Initial-Admin-Zugang — einmalig anzeigbar (read-once). */}
      <div className="bg-white rounded shadow p-4 mb-6">
        <h3 className="font-semibold mb-2">Initial-Admin-Zugang</h3>
        {adminCreds ? (
          <div>
            <div className="bg-amber-50 border border-amber-300 text-amber-800 text-sm rounded p-3 mb-3">
              ⚠️ Diese Zugangsdaten werden <strong>nur dieses eine Mal</strong> angezeigt und sind danach
              nicht mehr abrufbar. Jetzt sicher an den Kunden übergeben. Das Passwort ist temporär — der
              Kunde muss es beim ersten Login ändern (und ein TOTP-MFA einrichten).
            </div>
            <dl className="text-sm space-y-2">
              <div className="flex items-center gap-2">
                <dt className="w-24 text-slate-500">Benutzer</dt>
                <dd className="font-mono break-all">{adminCreds.username}</dd>
              </div>
              <div className="flex items-center gap-2">
                <dt className="w-24 text-slate-500">Passwort</dt>
                <dd className="font-mono break-all bg-slate-100 px-2 py-1 rounded">{adminCreds.password}</dd>
                <button
                  type="button"
                  className="px-2 py-1 rounded border text-xs"
                  onClick={() => navigator.clipboard?.writeText(adminCreds.password ?? '')}
                >Kopieren</button>
              </div>
              {adminCreds.accountUrl && (
                <div className="flex items-center gap-2">
                  <dt className="w-24 text-slate-500">Login</dt>
                  <dd className="font-mono break-all">
                    <a href={adminCreds.accountUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline">{adminCreds.accountUrl}</a>
                  </dd>
                </div>
              )}
            </dl>
          </div>
        ) : (
          <div>
            <p className="text-sm text-slate-500 mb-2">
              Zeigt das vom Provisioning generierte Initial-Passwort des Tenant-Admins —
              <strong> einmalig</strong>. Danach ist es aus Sicherheitsgründen nicht mehr abrufbar.
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={revealAdminCredentials}
                disabled={busy || isReadOnly}
                className="btn-primary"
              >Zugang anzeigen (einmalig)</button>
              <button
                type="button"
                onClick={resetAdminCreds}
                disabled={busy || isReadOnly}
                className="px-2 py-1 rounded border"
                title="Rotiert das Initial-Passwort via AWX, falls der Kunde es vor dem ersten Login verloren hat"
              >Zugang neu ausstellen</button>
            </div>
            {credsMsg && <p className="text-xs text-slate-500 mt-2">{credsMsg}</p>}
            {isReadOnly && <p className="text-xs text-slate-400 mt-2">Read-only-Operator; Zugangsdaten-Abruf deaktiviert.</p>}
          </div>
        )}
      </div>

      {/* DNS-Einträge (Core-Networks) — manuell anzulegen, bis Automatisierung aktiv. */}
      {dns && (
        <div className="bg-white rounded shadow p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold">DNS-Einträge (Core-Networks)</h3>
            <button
              type="button"
              className="px-2 py-1 rounded border text-xs"
              onClick={() => navigator.clipboard?.writeText(
                dns.records.map((r) => `${r.host}\tIN\t${r.type}\t${r.data}\t; TTL ${r.ttl}`).join('\n'),
              )}
            >Alles kopieren</button>
          </div>
          <p className="text-sm text-slate-500 mb-3">
            Diese Records in der Zone <span className="font-mono">{dns.zone}</span> bei <strong>Core-Networks</strong>
            anlegen <strong>und committen</strong>, damit der Tenant erreichbar wird. (Wird später automatisch erzeugt.)
            „Name" ist relativ zur Zone, Ziel ist der Cluster-Ingress.
          </p>
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-left">
              <tr>
                <th className="p-2">Name</th><th className="p-2">FQDN</th><th className="p-2">Typ</th>
                <th className="p-2">Ziel</th><th className="p-2">TTL</th><th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {dns.records.map((r) => (
                <tr key={r.fqdn} className="border-t">
                  <td className="p-2 font-mono">{r.host}</td>
                  <td className="p-2 font-mono text-slate-500">{r.fqdn}</td>
                  <td className="p-2 font-mono">{r.type}</td>
                  <td className="p-2 font-mono">{r.data}</td>
                  <td className="p-2 font-mono text-slate-500">{r.ttl}</td>
                  <td className="p-2">
                    <button
                      type="button"
                      className="px-2 py-1 rounded border text-xs"
                      onClick={() => navigator.clipboard?.writeText(`${r.host}\t${r.type}\t${r.data}`)}
                    >Kopieren</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Custom-Domain (BYO) — Kunden-eigene Domain anhängen. */}
      <div className="bg-white rounded shadow p-4 mb-6">
        <h3 className="font-semibold mb-2">Custom-Domain (BYO)</h3>
        <p className="text-sm text-slate-500 mb-2">
          Hängt eine kundeneigene Domain (z. B. <span className="font-mono">cloud.kunde.de</span>) an —
          Zertifikat (ACME) + Route + Nextcloud-trusted_domains. <strong>Voraussetzung:</strong> die Domain
          zeigt bereits per A/CNAME auf den Cluster-Ingress. <span className="font-mono">*.{domain}</span>
          wird abgelehnt (Produkt-Subdomains laufen automatisch).
        </p>
        <div className="flex flex-wrap gap-2 items-center">
          <input
            value={domainInput}
            onChange={(e) => setDomainInput(e.target.value)}
            placeholder="cloud.kunde.de"
            className="px-2 py-1 border rounded font-mono"
            disabled={busy || isReadOnly}
          />
          <button type="button" onClick={attachDomain} disabled={busy || isReadOnly || !domainInput.trim()} className="btn-primary">
            Domain anhängen
          </button>
        </div>
        {domainMsg && <p className="text-xs text-slate-500 mt-2">{domainMsg}</p>}
        {isReadOnly && <p className="text-xs text-slate-400 mt-2">Read-only-Operator; Domain-Anhängen deaktiviert.</p>}
      </div>

      {/* Service-Pakete (Achse B) — Core + Module/Bundles aktivieren. */}
      <ServicePackagePanel slug={tenant.slug} isReadOnly={isReadOnly} />

      {/* Abrechnung & Kosten — Erlös/Selbstkosten/Marge, Nutzung vs. Quota, kaufmännische Einstellungen. */}
      <BillingSection slug={tenant.slug} isReadOnly={isReadOnly} />

      {/* VDI / Linux Terminal Server — Guacamole + Desktops, Warm-Pool, Kosten. */}
      <VdiSection slug={tenant.slug} isReadOnly={isReadOnly} />

      {/* Webhosting (ADR 0015) — Websites, Domains, Datenbanken, DNS, Dateizugang je Mandant. */}
      <WebhostingSection slug={tenant.slug} isReadOnly={isReadOnly} />

      {/* MSP-Zuordnung — Marke (N3-5) + Provisioning-Kontext (N3-4). */}
      <TenantAssignmentSection
        slug={tenant.slug}
        currentBrand={tenant.brand}
        currentContextId={tenant.contextId}
        currentCustomerId={tenant.customerId}
        isReadOnly={isReadOnly}
        onChanged={load}
      />

      {/* Vertrag (N2-3) — S3-Buckets, AI-Token- + Storage-Quota. */}
      <ContractSection slug={tenant.slug} isReadOnly={isReadOnly} />

      {/* Lizenz (N2-2) — Seat-Limit + Auslastung. */}
      <LicenseSection slug={tenant.slug} isReadOnly={isReadOnly} />

      {/* KI-Anbieter (ADR 0021, DPA-015) — Nur lokal (Standard) / EU-LLM erlauben. */}
      <AiSettingsSection slug={tenant.slug} isReadOnly={isReadOnly} />

      {/* Welle 4.6 NS-2 — per-tenant branding for the tenant-admin portal. */}
      <BrandingSection slug={tenant.slug} isReadOnly={isReadOnly} />

      <h3 className="font-semibold mb-2">Plan-Historie</h3>
      <table className="w-full bg-white shadow rounded mb-6 text-sm">
        <thead className="bg-slate-100 text-left">
          <tr><th className="p-2">Datum</th><th className="p-2">Von</th><th className="p-2">Nach</th><th className="p-2">Begründung</th></tr>
        </thead>
        <tbody>
          {tenant.planHistory.map((h) => (
            <tr key={h.id} className="border-t">
              <td className="p-2 text-slate-500">{new Date(h.createdAt).toLocaleString()}</td>
              <td className="p-2">{h.fromPlan ?? '—'}</td>
              <td className="p-2"><strong>{h.toPlan}</strong></td>
              <td className="p-2 text-slate-600">{h.reason ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 className="font-semibold mb-2">AWX-Jobs</h3>
      <table className="w-full bg-white shadow rounded text-sm">
        <thead className="bg-slate-100 text-left">
          <tr><th className="p-2">Job-ID</th><th className="p-2">Template</th><th className="p-2">Status</th><th className="p-2">Gestartet</th><th className="p-2">Beendet</th></tr>
        </thead>
        <tbody>
          {tenant.awxJobs.map((j) => (
            <tr key={j.id} className="border-t">
              <td className="p-2 font-mono">{j.id}</td>
              <td className="p-2">{j.template}</td>
              <td className="p-2"><span className="badge bg-slate-200">{j.status}</span></td>
              <td className="p-2 text-slate-500">{j.startedAt ? new Date(j.startedAt).toLocaleString() : '—'}</td>
              <td className="p-2 text-slate-500">{j.finishedAt ? new Date(j.finishedAt).toLocaleString() : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex items-center justify-between mt-6 mb-2">
        <h3 className="font-semibold">CTI-Deployments</h3>
        <Link to="/cti-deployments/new" className="text-primary-600 hover:underline text-sm">
          + CTI deployen
        </Link>
      </div>
      <table className="w-full bg-white shadow rounded text-sm">
        <thead className="bg-slate-100 text-left">
          <tr><th className="p-2">Job-ID</th><th className="p-2">VM-Template</th><th className="p-2">Profile</th><th className="p-2">Status</th><th className="p-2">Host</th><th className="p-2">Gestartet</th></tr>
        </thead>
        <tbody>
          {tenant.ctiDeployments.map((d) => (
            <tr key={d.id} className="border-t">
              <td className="p-2 font-mono">
                <Link to="/cti-deployments" className="text-primary-600 hover:underline">{d.id}</Link>
              </td>
              <td className="p-2 font-mono text-xs">{d.vmTemplate}</td>
              <td className="p-2 text-xs">{d.profiles.join(', ')}</td>
              <td className="p-2"><span className="badge bg-slate-200">{d.status}</span></td>
              <td className="p-2 font-mono text-xs">{d.host ?? '—'}</td>
              <td className="p-2 text-slate-500">{d.startedAt ? new Date(d.startedAt).toLocaleString() : '—'}</td>
            </tr>
          ))}
          {tenant.ctiDeployments.length === 0 && (
            <tr><td colSpan={6} className="p-3 text-center text-slate-400">Keine CTI-Deployments für diesen Tenant.</td></tr>
          )}
        </tbody>
      </table>

      {pendingPlan && pendingPlan !== tenant.plan && (
        <PlanChangeCostModal
          currentPlan={tenant.plan}
          targetPlan={pendingPlan}
          plans={plans}
          busy={busy}
          onConfirm={(reason) => changePlan(pendingPlan as string, reason)}
          onCancel={() => setPendingPlan(null)}
        />
      )}
    </div>
  );
}
